import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageSquare, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Search,
  Filter,
  Download,
  Eye,
  Flag,
  Book,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const conversationLogs = [
  { id: 1, patient: 'Ahmad Ismail', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100', topic: 'Exercise clarification', messages: 12, duration: '8 min', status: 'resolved', escalated: false, time: '10:23 AM' },
  { id: 2, patient: 'Sarah Lee', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100', topic: 'Pain management query', messages: 8, duration: '5 min', status: 'escalated', escalated: true, time: '9:45 AM' },
  { id: 3, patient: 'Ravi Kumar', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100', topic: 'Appointment rescheduling', messages: 6, duration: '3 min', status: 'resolved', escalated: false, time: '9:12 AM' },
  { id: 4, patient: 'Mei Ling Wong', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100', topic: 'Medication question', messages: 4, duration: '2 min', status: 'escalated', escalated: true, time: '8:55 AM' },
  { id: 5, patient: 'David Tan', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100', topic: 'Progress inquiry', messages: 15, duration: '10 min', status: 'resolved', escalated: false, time: '8:30 AM' },
];

const escalations = [
  { id: 1, patient: 'Sarah Lee', reason: 'Medical advice requested beyond AI scope', assignedTo: 'Dr. Lisa Chen', status: 'in_progress', priority: 'high', time: '2 hours ago' },
  { id: 2, patient: 'Mei Ling Wong', reason: 'Patient reported severe side effects', assignedTo: 'Care Team', status: 'open', priority: 'urgent', time: '3 hours ago' },
  { id: 3, patient: 'John Ng', reason: 'Emergency contact request', assignedTo: 'Support Team', status: 'resolved', priority: 'medium', time: 'Yesterday' },
];

const flaggedMessages = [
  { id: 1, message: 'I\'ve been having chest pains during exercises', patient: 'Ahmad Ismail', flag: 'Medical concern', severity: 'high', action: 'Escalated to therapist' },
  { id: 2, message: 'Can you prescribe me stronger painkillers?', patient: 'Priya Devi', flag: 'Out of scope', severity: 'medium', action: 'Redirected to doctor' },
  { id: 3, message: 'The exercises are making my condition worse', patient: 'Michael Ooi', flag: 'Treatment concern', severity: 'high', action: 'Flagged for review' },
];

const knowledgeBase = [
  { category: 'Exercise Instructions', articles: 156, coverage: 94, lastUpdated: '2 days ago' },
  { category: 'Pain Management', articles: 78, coverage: 88, lastUpdated: '1 week ago' },
  { category: 'Recovery FAQ', articles: 234, coverage: 96, lastUpdated: '3 days ago' },
  { category: 'Appointment Policies', articles: 45, coverage: 100, lastUpdated: '1 day ago' },
  { category: 'Emergency Protocols', articles: 32, coverage: 100, lastUpdated: 'Today' },
];

export default function AIAgentOversight() {
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogDialog, setShowLogDialog] = useState(false);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">AI Agent Oversight</h1>
          <p className="text-sm text-muted-foreground mt-1">Monitor and manage AI conversations and escalations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Logs
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Conversations', value: '1,247', subtitle: 'Today', icon: MessageSquare, color: 'text-primary' },
          { label: 'Resolution Rate', value: '94.2%', subtitle: '+2.1% vs yesterday', icon: CheckCircle, color: 'text-success' },
          { label: 'Escalations', value: '72', subtitle: '5.8% of total', icon: AlertTriangle, color: 'text-warning' },
          { label: 'Avg Response Time', value: '1.2s', subtitle: '-0.3s vs avg', icon: Clock, color: 'text-info' },
        ].map((stat, idx) => (
          <Card key={idx}>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl font-display font-bold text-foreground mt-1">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Main Tabs */}
      <Tabs defaultValue="conversations">
        <TabsList>
          <TabsTrigger value="conversations">Conversations</TabsTrigger>
          <TabsTrigger value="escalations">Escalations</TabsTrigger>
          <TabsTrigger value="flagged">Flagged Messages</TabsTrigger>
          <TabsTrigger value="knowledge">Knowledge Base</TabsTrigger>
        </TabsList>
        
        <TabsContent value="conversations" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="text-base">Conversation Audit Log</CardTitle>
                  <CardDescription>Review AI-patient conversations</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search conversations..." className="pl-9 w-64" />
                  </div>
                  <Button variant="outline" size="icon">
                    <Filter className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Topic</TableHead>
                    <TableHead>Messages</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conversationLogs.map((log) => (
                    <TableRow key={log.id} className="cursor-pointer" onClick={() => { setSelectedLog(log); setShowLogDialog(true); }}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={log.avatar} />
                            <AvatarFallback>{log.patient.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{log.patient}</span>
                        </div>
                      </TableCell>
                      <TableCell>{log.topic}</TableCell>
                      <TableCell>{log.messages}</TableCell>
                      <TableCell>{log.duration}</TableCell>
                      <TableCell>
                        <Badge variant={log.escalated ? 'destructive' : 'default'}>
                          {log.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{log.time}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="escalations" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Escalation Queue</CardTitle>
              <CardDescription>Conversations requiring human intervention</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {escalations.map((esc, idx) => (
                <motion.div
                  key={esc.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-4 rounded-lg border border-border"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Badge variant={esc.priority === 'urgent' ? 'destructive' : esc.priority === 'high' ? 'default' : 'secondary'}>
                        {esc.priority}
                      </Badge>
                      <span className="font-medium text-foreground">{esc.patient}</span>
                    </div>
                    <Badge variant="outline">{esc.status.replace('_', ' ')}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{esc.reason}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Assigned to: {esc.assignedTo} • {esc.time}</span>
                    <Button size="sm" variant="outline">View Details</Button>
                  </div>
                </motion.div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="flagged" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Flag className="w-4 h-4 text-primary" />
                Flagged Messages
              </CardTitle>
              <CardDescription>Messages automatically flagged for review</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Message</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Flag Type</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Action Taken</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flaggedMessages.map((msg) => (
                    <TableRow key={msg.id}>
                      <TableCell className="max-w-[300px]">
                        <p className="text-sm truncate">"{msg.message}"</p>
                      </TableCell>
                      <TableCell>{msg.patient}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{msg.flag}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={msg.severity === 'high' ? 'destructive' : 'default'}>{msg.severity}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{msg.action}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="knowledge" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Book className="w-4 h-4" />
                    Knowledge Base Coverage
                  </CardTitle>
                  <CardDescription>AI knowledge sources and coverage metrics</CardDescription>
                </div>
                <Button>Manage Content</Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Articles</TableHead>
                    <TableHead>Query Coverage</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {knowledgeBase.map((kb, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{kb.category}</TableCell>
                      <TableCell>{kb.articles}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-success rounded-full"
                              style={{ width: `${kb.coverage}%` }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground">{kb.coverage}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{kb.lastUpdated}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">Edit</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Conversation Detail Dialog */}
      <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Conversation Log</DialogTitle>
            <DialogDescription>
              {selectedLog?.patient} • {selectedLog?.topic}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {/* Sample conversation */}
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="chat-bubble-ai">
                <p className="text-sm">Hello! How can I help you today with your recovery?</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <div className="chat-bubble-user">
                <p className="text-sm">I'm confused about the leg exercises. Which one should I do first?</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="chat-bubble-ai">
                <p className="text-sm">Great question! Let me walk you through the order. Start with the gentle stretches (Exercise 1), hold for 30 seconds each side. Then move to the strengthening exercises (Exercise 2-4).</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
